import React from "react";
import _ from "lodash";
import ReactTooltip from "react-tooltip";

import UnitFullPath from "components/common/UnitFullPath";
import ClickToCopyWrapper from "components/common/ClickToCopyWrapper";
import SimpleTooltip from "components/common/SimpleTooltip";
import UnitMode from "components/common/UnitMode";
import ItemsFilter from "components/ops-mgmt/common/ItemsFilter";
import DemandTypePortfolioButton from "components/common/DemandTypePortfolioButton";
import SelectedStickyFooter from "components/common/SelectedStickyFooter";
import UnitStatus from "components/common/UnitStatus";
import OptionMultiList from "components/common/OptionMultiList";

import OptionSelector from "./OptionSelector";
import AllSelectBtn from "./AllSelectBtn";

import { exprParse } from "helpers/ExprEval";
import EquationHintModal from "./EquationHintModal";
import AttributeHintModal from "./AttributeHintModal";

import { parseTagsToObject } from "./DataHelper";
import {
  YIELD_SET,
  UNIT_MODE,
  UNIT_STATUS,
  SPECIAL_UNIT_TYPE,
  REQUEST_TYPE,
  SEARCH_KEYS,
  MATCH,
} from "./Utils";

import { FiInfo } from "react-icons/fi";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";

class UnitsSelectionTable extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      filterMode: UNIT_MODE.ALL,
      filterStatus: UNIT_STATUS.ALL,
      filterYeildSet: YIELD_SET.ALL,
      filterSpecialUnits: SPECIAL_UNIT_TYPE.ALL,
      filterRequestType: REQUEST_TYPE.ALL,
      switchMatch: MATCH.ANY,

      optionYieldSet: {},
      optionMode: {},
      optionStatus: {},
      optionSpecials: {},
      optionRequestType: {},

      selectedUnitIds: [],
      selectedKey: SEARCH_KEYS.UNIT_ID,

      filteredUnits: this.props.units,

      isOpenEquationHintModal: false,
      isOpenAttributeHintModal: false,
      isFolding: true,
    };

    this.searchRef = React.createRef();

    this.handleYieldSetToOptions = this.handleYieldSetToOptions.bind(this);
    this.handleSelectUnit = this.handleSelectUnit.bind(this);
    this.handleClearSelected = this.handleClearSelected.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleClearAll = this.handleClearAll.bind(this);
    this.handleOptionChanged = this.handleOptionChanged.bind(this);
    this.handleSelectedKeyChanged = this.handleSelectedKeyChanged.bind(this);
  }

  componentDidMount() {
    const stateData = {
      optionYieldSet: this.handleYieldSetToOptions(),
      optionRequestType: this.handleRequestTypeToOptions(),
    };
    this.setState(stateData);
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.units, this.props.units)) {
      const stateData = {
        optionYieldSet: this.handleYieldSetToOptions(),
        optionRequestType: this.handleRequestTypeToOptions(),
      };
      this.setState(stateData);
    }
  }

  handleYieldSetToOptions() {
    const { units, ysData } = this.props;

    const unitByYieldSet = _.countBy(units, "yieldSet.yieldSetId");
    const ysDataMap = _.keyBy(ysData, "yieldSetId");
    const optionYieldSet = {
      [YIELD_SET.ALL]: { count: units.length },
      ..._.mapValues(ysDataMap, ({ yieldSetId, name }) => ({
        count: unitByYieldSet[yieldSetId] || 0,
        text: name,
      })),
    };

    return optionYieldSet;
  }

  handleRequestTypeToOptions() {
    const { units } = this.props;

    const counts = {};
    units.forEach((u) => {
      (u.requestTypes || []).forEach((rt) => {
        if (rt) {
          counts[rt] = (counts[rt] || 0) + 1;
        }
      });
    });

    const optionRequestType = {
      [REQUEST_TYPE.ALL]: { count: units.length },
      ..._.mapValues(counts, (count, text) => ({
        count,
        text,
      })),
    };

    return optionRequestType;
  }

  handleClearAll() {
    // for parent
    this.handleClearSelected();

    this.handleOptionChanged({
      filterMode: UNIT_MODE.ALL,
      filterStatus: UNIT_STATUS.ALL,
      filterYeildSet: YIELD_SET.ALL,
      filterSpecialUnits: SPECIAL_UNIT_TYPE.ALL,
      filterRequestType: REQUEST_TYPE.ALL,
    });

    if (this.searchRef.current) {
      this.searchRef.current.handleClearInput();
    }
  }

  handleSelectUnit(unitId) {
    const { selectedUnitIds, filteredUnits } = this.state;
    if (unitId === "ALL") {
      if (
        selectedUnitIds.length > 0 &&
        filteredUnits.length === selectedUnitIds.length
      ) {
        // unselect all
        this.setState({ selectedUnitIds: [] });
      } else {
        const newUnits = _.map(filteredUnits, (u) => {
          return u.adUnitId;
        });
        this.setState({ selectedUnitIds: newUnits });
      }
    } else {
      let newUnits = [];
      if (_.includes(selectedUnitIds, unitId)) {
        newUnits = _.filter(selectedUnitIds, (uid) => {
          return uid !== unitId;
        });
      } else {
        newUnits = [...selectedUnitIds, unitId];
      }
      this.setState({ selectedUnitIds: newUnits });
    }
  }

  handleClearSelected() {
    this.setState({ selectedUnitIds: [] });
  }

  handleOptionChanged(param) {
    this.setState(param, () => {
      this.handleSearch();
    });
  }

  handleSelectedKeyChanged(key) {
    this.setState({ selectedKey: key });
  }

  handleSearch(value) {
    if (value === undefined) {
      if (this.searchRef.current) {
        const inputValue = this.searchRef.current.state.inputValue;
        this.execSearch(inputValue);
      }
    } else {
      this.execSearch(value);
    }
  }

  execSearch(inputValue) {
    const {
      filterMode,
      filterStatus,
      filterYeildSet,
      filterSpecialUnits,
      filterRequestType,
      switchMatch,
    } = this.state;

    const yslist =
      filterYeildSet !== YIELD_SET.ALL
        ? _.map(filterYeildSet, (ys) => _.toInteger(ys))
        : [];

    const optionMode = {
      ..._.mapValues(UNIT_MODE, () => ({
        count: 0,
      })),
      [UNIT_MODE.ALL]: { count: 0 },
    };

    const optionStatus = {
      ..._.mapValues(UNIT_STATUS, () => ({
        count: 0,
      })),
      [UNIT_STATUS.ALL]: { count: 0, text: "ALL" },
    };

    const optionSpecials = {
      [SPECIAL_UNIT_TYPE.EXP]: { count: 0, text: "Explicitly Targeted" },
      [SPECIAL_UNIT_TYPE.ANC_EXP]: {
        count: 0,
        text: "Has Explicitly Targeted Ancestor",
      },
      [SPECIAL_UNIT_TYPE.ALL]: { count: 0, text: "ALL" },
    };

    const optionRequestType = _.mapValues(
      this.state.optionRequestType,
      (value, key) => {
        return {
          ...value,
          count: 0,
        };
      }
    );

    const filterUnits = this.props.units.filter((unit) => {
      // [yieldset] filter
      if (
        filterYeildSet !== YIELD_SET.ALL &&
        !_.includes(yslist, unit.yieldSet.yieldSetId)
      ) {
        return false;
      }

      // [mode] setup
      if (_.isNil(unit.mode)) return false;
      _.update(optionMode, [unit.mode, "count"], (count) => (count || 0) + 1);
      _.update(
        optionMode,
        [UNIT_MODE.ALL, "count"],
        (count) => (count || 0) + 1
      );

      // [mode] filter
      if (filterMode !== UNIT_MODE.ALL && unit.mode !== filterMode) {
        return false;
      }

      // [status] setup
      if (_.isNil(unit.status)) return false;
      _.update(
        optionStatus,
        [UNIT_STATUS.ALL, "count"],
        (count) => (count || 0) + 1
      );
      _.update(
        optionStatus,
        [unit.status, "count"],
        (count) => (count || 0) + 1
      );

      // [status] filter
      if (filterStatus !== UNIT_STATUS.ALL && unit.status !== filterStatus) {
        return false;
      }

      // [special type] setup
      _.update(
        optionSpecials,
        [SPECIAL_UNIT_TYPE.ALL, "count"],
        (count) => (count || 0) + 1
      );

      if (unit.explicitlyTargeted) {
        _.update(
          optionSpecials,
          [SPECIAL_UNIT_TYPE.EXP, "count"],
          (count) => (count || 0) + 1
        );
      }
      if (unit.hasExplicitlyTargetedAncestor) {
        _.update(
          optionSpecials,
          [SPECIAL_UNIT_TYPE.ANC_EXP, "count"],
          (count) => (count || 0) + 1
        );
      }

      // [special type] filter
      if (filterSpecialUnits !== SPECIAL_UNIT_TYPE.ALL) {
        if (
          filterSpecialUnits === SPECIAL_UNIT_TYPE.EXP &&
          unit.explicitlyTargeted !== true
        ) {
          return false;
        }
        if (
          filterSpecialUnits === SPECIAL_UNIT_TYPE.ANC_EXP &&
          unit.hasExplicitlyTargetedAncestor !== true
        ) {
          return false;
        }
      }

      // [request type] setup
      _.update(
        optionRequestType,
        [REQUEST_TYPE.ALL, "count"],
        (count) => (count || 0) + 1
      );

      (unit.requestTypes || []).forEach((rt) => {
        if (rt) {
          _.update(
            optionRequestType,
            [rt, "count"],
            (count) => (count || 0) + 1
          );
        }
      });

      // [request type] filter
      if (filterRequestType !== REQUEST_TYPE.ALL) {
        let requestResult = false;
        switch (switchMatch) {
          case MATCH.ALL:
            requestResult =
              _.every(filterRequestType, (rt) =>
                _.includes(unit.requestTypes || [], rt)
              ) || filterRequestType.length === 0;
            break;
          case MATCH.ANY:
            requestResult =
              _.intersection(unit.requestTypes || [], filterRequestType)
                .length > 0;
            break;
          case MATCH.NONE:
            requestResult =
              _.intersection(unit.requestTypes || [], filterRequestType)
                .length === 0;
            break;
          case MATCH.ONLY:
            requestResult = _.isEqual(
              _.sortBy(unit.requestTypes || []),
              _.sortBy(filterRequestType)
            );
            break;
          default:
            break;
        }

        if (!requestResult) {
          return false;
        }
      }

      return true;
    });

    const stateData = {
      optionMode,
      optionStatus,
      optionSpecials,
      optionRequestType,
      expErrMsg: null,
    };

    if (inputValue === "") {
      this.setState({ ...stateData, filteredUnits: filterUnits });
      return;
    }

    let newUnits = [];

    try {
      const expr = exprParse(inputValue);

      newUnits = _.filter(filterUnits, (params) => {
        const {
          tags,
          name,
          adUnitId,
          extGamAdUnitId,
          parentPath,
          siteNetworkCode,
        } = params;

        const symbolObj = _.reduce(
          expr.symbols(),
          (acc, symbol) => {
            return {
              ...acc,
              [symbol]: false,
            };
          },
          {}
        );

        const tagsObj = {
          ...symbolObj,
          ...parseTagsToObject(tags),
          name,
          adUnitId,
          extGamAdUnitId,
          parentPath,
          siteNetworkCode: siteNetworkCode || undefined,
        };
        return expr.evaluate(tagsObj);
      });
    } catch (err) {
      stateData.expErrMsg = err.message;
    }

    this.setState({
      ...stateData,
      filteredUnits: newUnits,
    });
  }

  getSelectedUnitsByKey(key) {
    // key: same as searchKey
    const { units } = this.props;
    const { selectedUnitIds } = this.state;

    let results = _.filter(units, (u) => {
      return _.indexOf(selectedUnitIds, u.adUnitId) !== -1;
    });

    results = _.map(results, key);
    return results;
  }

  render() {
    const { units, tags } = this.props;
    const {
      isOpenEquationHintModal,
      isOpenAttributeHintModal,
      isFolding,
      selectedUnitIds,
      selectedKey,
      filteredUnits,
      filterMode,
      filterStatus,
      filterYeildSet,
      filterSpecialUnits,
      filterRequestType,
      switchMatch,
      optionMode,
      optionStatus,
      optionYieldSet,
      optionSpecials,
      optionRequestType,
      expErrMsg,
    } = this.state;

    if (units.length === 0) {
      return "No units found";
    }

    const selectedItems = this.getSelectedUnitsByKey(selectedKey);
    const selectedExtGamUnitIds = this.getSelectedUnitsByKey(
      SEARCH_KEYS.EXT_UNIT_ID
    );
    const selectedItemsText = selectedExtGamUnitIds.join(", ");

    return (
      <>
        <div className="border rounded mb-1 p-4 text-xs font-semibold">
          <div className="mb-2 flex gap-2">
            <div className="w-20 pt-1">
              Yieldset ({_.keys(optionYieldSet).length - 1}):{" "}
            </div>
            <div className="flex-1">
              <OptionMultiSelector
                selected={filterYeildSet}
                options={optionYieldSet}
                allKey={YIELD_SET.ALL}
                handleSelectChanged={(value) =>
                  this.handleOptionChanged({ filterYeildSet: value })
                }
              ></OptionMultiSelector>
            </div>
          </div>
          <div className="mb-2 flex gap-2">
            <div className="w-20 pt-1">Unit Mode: </div>
            <OptionSelector
              selected={filterMode}
              options={optionMode}
              allKey={UNIT_MODE.ALL}
              // standaloneSelectAll={true}
              handleSelectChanged={(mode) => {
                this.handleOptionChanged({ filterMode: mode });
              }}
            ></OptionSelector>
          </div>

          <div className="mb-2 flex gap-2">
            <div className="w-20 pt-1">Unit Status: </div>
            <OptionSelector
              selected={filterStatus}
              options={optionStatus}
              allKey={UNIT_STATUS.ALL}
              // standaloneSelectAll={true}
              handleSelectChanged={(status) => {
                this.handleOptionChanged({ filterStatus: status });
              }}
            ></OptionSelector>
          </div>

          <div className="mb-2 flex gap-2">
            <div className="w-20 pt-1">Special: </div>
            <OptionSelector
              selected={filterSpecialUnits}
              options={optionSpecials}
              allKey={SPECIAL_UNIT_TYPE.ALL}
              // standaloneSelectAll={true}
              handleSelectChanged={(special) => {
                this.handleOptionChanged({ filterSpecialUnits: special });
              }}
            ></OptionSelector>
          </div>

          <div className="flex gap-2">
            <div className="w-24 pt-1">Request Type: </div>
            <div className="flex-1">
              <OptionMultiSelector
                selected={filterRequestType}
                match={switchMatch}
                options={optionRequestType}
                isSearchable={false}
                isSwitchMatch={true}
                allKey={REQUEST_TYPE.ALL}
                handleSelectChanged={(value) =>
                  this.handleOptionChanged({ filterRequestType: value })
                }
                handleMatchChanged={(value) =>
                  this.handleOptionChanged({ switchMatch: value })
                }
              ></OptionMultiSelector>
            </div>
          </div>
          <div className="mb-2 text-xs text-gray-600">
            * The unit’s request type will be determined if they have request
            type data of the past 7 days.
          </div>

          <div className="border-t -mx-4 mb-1 mt-4 flex gap-2 px-4 pt-1 text-sm">
            <div className="pt-1">Advanced Search: </div>
          </div>

          <div className="mb-1 flex gap-4">
            <div className="flex text-sm text-gray-700">
              - Enter specific parameters to perform exact or partial matching.{" "}
              <div
                className="ml-1 flex cursor-pointer items-center text-blue-600"
                onClick={() => {
                  this.setState({
                    isOpenAttributeHintModal: true,
                  });
                }}
              >
                <FiInfo data-tip data-for="attribute-description"></FiInfo>
              </div>
              <ReactTooltip
                id="attribute-description"
                type="dark"
                effect="solid"
                place="top"
              >
                Click to see how to perform exact or partial matching
              </ReactTooltip>
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              For example: name CONTAINS "feed"{" "}
              <ClickToCopyWrapper
                copyText={`name CONTAINS "feed"`}
              ></ClickToCopyWrapper>
            </div>
          </div>

          <div className="mb-1 flex gap-4 pl-3 text-xs">
            <div className="flex gap-2">
              {_.values(SEARCH_KEYS).map((value) => {
                if (_.includes([SEARCH_KEYS.TAGS], value)) return "";

                return (
                  <div
                    key={value}
                    className="rounded border cursor-pointer px-2 text-blue-700 hover:bg-blue-200"
                    onClick={() => {
                      if (this.searchRef.current) {
                        this.searchRef.current.insertInputValue(value);
                      }
                    }}
                  >
                    {value}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              {["EQUALS", "CONTAINS"].map((value) => {
                return (
                  <div
                    key={value}
                    className="rounded border cursor-pointer px-2 text-center text-gray-700 hover:bg-gray-300"
                    style={{ minWidth: "1.5rem" }}
                    onClick={() => {
                      if (this.searchRef.current) {
                        let inputValue = "";
                        if (value === "EQUALS") {
                          inputValue = `EQUALS ""`;
                        }
                        if (value === "CONTAINS") {
                          inputValue = `CONTAINS ""`;
                        }
                        this.searchRef.current.insertInputValue(
                          inputValue,
                          inputValue.length - 1,
                          false
                        );
                      }
                    }}
                  >
                    {value}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-1 text-sm text-gray-700">- Tags</div>
          <div className="-ml-1 mb-2 flex">
            <div
              className="cursor-pointer text-sm font-semibold text-orange-700 hover:text-orange-900"
              style={{ marginRight: "0.125rem" }}
              onClick={() => {
                this.setState({ isFolding: !isFolding });
              }}
            >
              {!isFolding ? (
                <FaAngleUp></FaAngleUp>
              ) : (
                <FaAngleDown></FaAngleDown>
              )}
            </div>
            <div
              className="flex flex-wrap gap-2 overflow-hidden text-xs transition-[max-height] duration-200 ease-in-out"
              style={{ maxHeight: isFolding ? "1.3rem" : "300px" }}
            >
              {tags.map((item) => (
                <div
                  key={item.tag}
                  className={`rounded border cursor-pointer px-1 text-center ${
                    item.count > 0
                      ? "text-orange-700 hover:bg-orange-200"
                      : "text-orange-500 hover:bg-orange-100"
                  }`}
                  style={{ minWidth: "1.5rem" }}
                  onClick={() => {
                    if (this.searchRef.current) {
                      this.searchRef.current.insertInputValue(item.tag, -1);
                    }
                  }}
                >
                  {item.tag} {item.count > 0 ? `(${item.count})` : ""}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-1">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div className="text-sm text-gray-700">
                  - Tags can be used with comparison operators.
                </div>
                <div
                  className="flex cursor-pointer items-center text-sm text-blue-600"
                  onClick={() => {
                    this.setState({
                      isOpenEquationHintModal: true,
                    });
                  }}
                >
                  <FiInfo data-tip data-for="equation-description"></FiInfo>
                </div>
                <ReactTooltip
                  id="equation-description"
                  type="dark"
                  effect="solid"
                  place="right"
                >
                  Click to see how to search using tags and operators
                </ReactTooltip>
              </div>

              <div className="flex items-center gap-1 text-gray-600">
                For example: {`DROP_OFF_RATE_VIDEO <= 50`}
                <ClickToCopyWrapper
                  copyText={`DROP_OFF_RATE_VIDEO <= 50`}
                ></ClickToCopyWrapper>
              </div>
              <div className="flex items-center gap-1 text-gray-600">
                Without tag "REFRESH": {`REFRESH == false`}
                <ClickToCopyWrapper
                  copyText={`REFRESH == false`}
                ></ClickToCopyWrapper>
              </div>
            </div>

            <div className="mb-1 flex gap-2 pl-3 text-xs">
              {["==", ">", "<", ">=", "<="].map((value) => {
                return (
                  <div
                    key={value}
                    className="rounded border cursor-pointer px-1 text-center font-semibold text-teal-700 hover:bg-teal-200"
                    style={{ minWidth: "1.5rem" }}
                    onClick={() => {
                      this.searchRef.current.insertInputValue(value);
                    }}
                  >
                    {value}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-2">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-700">
                - Multiple search conditions can be combined.
              </div>

              <div className="flex items-center gap-1 text-gray-600">
                For example: {`name CONTAINS "feed" AND REQ_PCT:UPA >= 50`}
                <ClickToCopyWrapper
                  copyText={`name CONTAINS "feed" AND REQ_PCT:UPA >= 50`}
                ></ClickToCopyWrapper>
              </div>
            </div>

            <div className="mb-1 flex gap-2 pl-3 text-xs">
              {["AND", "OR"].map((value) => {
                return (
                  <div
                    key={value}
                    className="rounded border cursor-pointer px-1 text-center font-semibold text-purple-700 hover:bg-purple-200"
                    style={{ minWidth: "1.5rem" }}
                    onClick={() => {
                      this.searchRef.current.insertInputValue(value);
                    }}
                  >
                    {value}
                  </div>
                );
              })}
            </div>
          </div>

          <ItemsFilter
            ref={this.searchRef}
            placeholder="Use AND, OR, ==, >, < to build your search conditions."
            handleSearch={this.handleSearch}
          ></ItemsFilter>
          {expErrMsg && <div className="text-xs text-red-600">{expErrMsg}</div>}
        </div>

        <div className="italic text-gray-800">
          Showing <b>{filteredUnits.length}</b> out of {units.length} units
        </div>

        <table
          className="border shadow table w-full text-sm"
          style={{ marginBottom: "500px" }}
        >
          <thead className="border-b bg-gray-200 text-xs text-blue-800">
            <tr>
              <th className="w-10 px-2 text-center">
                <input
                  style={{
                    width: "18px",
                    height: "18px",
                    marginTop: "4px",
                  }}
                  type="checkbox"
                  checked={selectedUnitIds.length === filteredUnits.length}
                  onChange={() => this.handleSelectUnit("ALL")}
                ></input>
              </th>
              <th className="border px-2 py-1">Unit</th>
            </tr>
          </thead>
          <tbody className="bg-white font-mono font-normal text-gray-900">
            {filteredUnits &&
              filteredUnits.map((r, i) => {
                return (
                  <tr key={r.adUnitId} className="border-b hover:bg-gray-100">
                    <td className="px-2 text-center">
                      <input
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "4px",
                        }}
                        type="checkbox"
                        checked={selectedUnitIds.indexOf(r.adUnitId) !== -1}
                        onChange={() => this.handleSelectUnit(r.adUnitId)}
                      ></input>
                    </td>
                    <td className="whitespace-no-wrap border px-2 py-1 font-sans font-semibold">
                      <div className="flex items-center gap-2">
                        <div className="border-b border-gray-600">
                          {r.adUnitId}{" "}
                          <span className="mr-1 text-gray-800">{r.name}</span>
                          <UnitStatus unit={r}></UnitStatus>
                          <UnitMode unit={r} version="v2"></UnitMode>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="font-mono text-xs text-gray-700">
                          {r.extGamAdUnitId}
                        </div>
                        <UnitFullPath
                          path={r.parentPath}
                          name={r.code}
                          replaceClass="text-xs font-normal text-gray-700"
                        ></UnitFullPath>
                      </div>

                      <div className="text-xs font-normal text-gray-700">
                        YS: {r.yieldSet.yieldSetId} {r.yieldSet.name}
                        {r.explicitlyTargeted && (
                          <SimpleTooltip
                            tooltipText="Explicitly Targeted"
                            place="bottom"
                          >
                            <span className="rounded ml-2 bg-blue-200 px-1 text-xs font-semibold text-blue-800">
                              EXPL
                            </span>
                          </SimpleTooltip>
                        )}
                        {r.hasExplicitlyTargetedAncestor && (
                          <SimpleTooltip
                            tooltipText="Has Explicitly Targeted Ancestor"
                            place="bottom"
                          >
                            <span className="rounded ml-2 bg-blue-200 px-1 text-xs font-semibold text-blue-800">
                              ANC EXPL
                            </span>
                          </SimpleTooltip>
                        )}
                      </div>

                      {r.siteNetworkCode && (
                        <div className="text-xs font-normal text-gray-600">
                          Site Network Code: {r.siteNetworkCode}
                        </div>
                      )}

                      <div
                        style={{ width: "100%" }}
                        className="w-full overflow-x-auto"
                      >
                        {r.requestTypes && (
                          <div className="whitespace-no-wrap flex flex-wrap gap-2">
                            {r.requestTypes.map((rt) => {
                              return (
                                <div
                                  key={`${r.adUnitId}-${rt}`}
                                  className="rounded bg-gray-200 px-2 text-xs text-gray-600"
                                >
                                  {rt}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div
                        style={{ width: "100%" }}
                        className="w-full overflow-x-auto"
                      >
                        {r.tags && (
                          <div className="whitespace-no-wrap my-1 flex flex-wrap gap-2">
                            {r.tags.map((t) => {
                              return (
                                <div
                                  key={t}
                                  className="rounded bg-orange-200 px-2 text-gray-600"
                                  style={{ fontSize: "12px" }}
                                >
                                  {t}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <SelectedStickyFooter
          idList={[SEARCH_KEYS.UNIT_ID, SEARCH_KEYS.EXT_UNIT_ID]}
          selectedItems={selectedItems}
          handleSelectedByKey={this.handleSelectedKeyChanged}
          handleClear={this.handleClearSelected}
          actionButtons={
            <DemandTypePortfolioButton
              networkId={this.props.networkId}
              gamUnitId={selectedItemsText}
            ></DemandTypePortfolioButton>
          }
        ></SelectedStickyFooter>

        {isOpenEquationHintModal && (
          <EquationHintModal
            handleClose={() =>
              this.setState({ isOpenEquationHintModal: false })
            }
          ></EquationHintModal>
        )}

        {isOpenAttributeHintModal && (
          <AttributeHintModal
            handleClose={() =>
              this.setState({ isOpenAttributeHintModal: false })
            }
          ></AttributeHintModal>
        )}
      </>
    );
  }
}

function OptionMultiSelector({
  options,
  selected,
  match,
  handleSelectChanged,
  handleMatchChanged,
  allKey = "",
  standaloneSelectAll = false,
  isSearchable = true,
  isSwitchMatch = false,
}) {
  const optKeyList = _.keys(options);
  const filteredList = _.filter(optKeyList, (key) => key !== allKey);
  const filteredOptions = _.pick(options, filteredList);

  return (
    <div className="flex">
      {options[allKey] && (
        <div className={standaloneSelectAll ? "w-full" : "flex-none"}>
          <AllSelectBtn
            value={selected}
            buttonText={`${options[allKey].text || allKey} ${
              options[allKey].count !== undefined
                ? `(${options[allKey].count})`
                : ""
            }`}
            handleClick={() => handleSelectChanged(allKey)}
          ></AllSelectBtn>
        </div>
      )}
      <div className="border-l ml-2 pl-2">
        <OptionMultiList
          selected={selected}
          match={match}
          allKey={allKey}
          isSearchable={isSearchable}
          isSwitchMatch={isSwitchMatch}
          options={filteredOptions}
          handleSelectChanged={handleSelectChanged}
          handleMatchChanged={handleMatchChanged}
        ></OptionMultiList>
      </div>
    </div>
  );
}

export default UnitsSelectionTable;
